import { useState, useEffect } from 'react'
import { X, RotateCcw, Clock, Trash2 } from 'lucide-react'
import { useBuilder } from '../context/BuilderContext'

const STORAGE_PREFIX = 'synxreach_versions_'

export function saveVersion(templateId, blocks, subject, bodySettings) {
  if (!templateId) return
  const key = STORAGE_PREFIX + templateId
  const versions = getVersions(templateId)
  const version = {
    id: `v-${Date.now()}`,
    timestamp: new Date().toISOString(),
    subject,
    blockCount: blocks.length,
    data: { blocks, subject, bodySettings }
  }
  versions.unshift(version)
  // Keep max 20 versions
  if (versions.length > 20) versions.length = 20
  localStorage.setItem(key, JSON.stringify(versions))
}

export function getVersions(templateId) {
  if (!templateId) return []
  try {
    const data = localStorage.getItem(STORAGE_PREFIX + templateId)
    return data ? JSON.parse(data) : []
  } catch {
    return []
  }
}

export default function VersionHistoryModal({ isOpen, onClose, templateId }) {
  const { setBlocks, setSubject, updateBodySettings } = useBuilder()
  const [versions, setVersions] = useState([])

  useEffect(() => {
    if (isOpen && templateId) {
      setVersions(getVersions(templateId))
    }
  }, [isOpen, templateId])

  const handleRestore = (version) => {
    if (confirm(`Restore this version from ${formatDate(version.timestamp)}? Current changes will be lost.`)) {
      setBlocks(version.data.blocks || [])
      setSubject(version.data.subject || '')
      if (version.data.bodySettings) {
        updateBodySettings(version.data.bodySettings)
      }
      onClose()
    }
  }

  const handleDeleteVersion = (versionId) => {
    const updated = versions.filter(v => v.id !== versionId)
    setVersions(updated)
    localStorage.setItem(STORAGE_PREFIX + templateId, JSON.stringify(updated))
  }

  const formatDate = (iso) => {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime()
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)'
    }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: '12px', width: '480px', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Clock size={18} style={{ color: 'var(--color-primary)' }} />
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Version History</h3>
          </div>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1.25rem' }}>
          {versions.length === 0 ? (
            <div style={{
              padding: '3rem 1rem', textAlign: 'center', color: '#94a3b8'
            }}>
              <Clock size={32} style={{ opacity: 0.4, marginBottom: '0.75rem' }} />
              <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '500', color: '#64748b' }}>
                No saved versions yet
              </p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem' }}>
                Versions are created each time you save the template
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {versions.map((version, idx) => (
                <div
                  key={version.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.75rem', borderRadius: '8px',
                    border: '1px solid #e5e7eb', background: idx === 0 ? '#f8fafc' : '#fff',
                    transition: 'all 0.15s'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = '#c7d2fe' }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e5e7eb' }}
                >
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '8px',
                    background: idx === 0 ? 'rgba(99,102,241,0.1)' : '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, color: idx === 0 ? 'var(--color-primary)' : '#94a3b8',
                    fontSize: '0.7rem', fontWeight: '700'
                  }}>
                    v{versions.length - idx}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: '500', color: '#1e293b' }}>
                      {version.subject || 'Untitled'}
                      {idx === 0 && (
                        <span style={{
                          marginLeft: '0.5rem', fontSize: '0.6rem', fontWeight: '600',
                          color: '#22c55e', background: '#f0fdf4', padding: '1px 6px',
                          borderRadius: '3px'
                        }}>
                          Latest
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '2px' }}>
                      {formatDate(version.timestamp)} ({timeAgo(version.timestamp)}) - {version.blockCount} blocks
                    </div>
                  </div>

                  <button
                    onClick={() => handleRestore(version)}
                    title="Restore this version"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.25rem',
                      padding: '0.3rem 0.6rem', border: '1px solid #c7d2fe',
                      borderRadius: '6px', background: '#fff', color: 'var(--color-primary)',
                      cursor: 'pointer', fontSize: '0.7rem', fontWeight: '500', flexShrink: 0
                    }}
                  >
                    <RotateCcw size={12} /> Restore
                  </button>

                  <button
                    onClick={() => handleDeleteVersion(version.id)}
                    title="Delete version"
                    style={{
                      display: 'flex', alignItems: 'center',
                      padding: '0.3rem', border: '1px solid #fecaca',
                      borderRadius: '6px', background: '#fff', color: '#dc2626',
                      cursor: 'pointer', flexShrink: 0
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
